import { Alert, AlertTitle, Box, Paper, useTheme } from "@mui/material";
import * as React from "react";

import { type MessagePathRecord } from "@/shared/domain/topics";
import { useMessagePathTablePreferences } from "@/shared/domain/users/hooks";
import { RobotoDomainException } from "@/shared/http/exceptions";
import { SearchQueryBody } from "@/types";

import { MessagePathsDetail } from "../MessagePathsDetail";
import { RobotoDataGrid } from "../RobotoDataGrid";

import { ColumnsModal } from "./ColumnsModal";
import { TablePageState, tablePageReducer } from "./stateReducer";
import { createRows, mergeMetadataColumns } from "./tableLogic";
import type { BaseTableProps } from "./tableProps";

const possibleStandardMsgPathsColumns = [
  "canonical_data_type",
  "created",
  "created_by",
  "data_type",
  "message_path",
  "modified",
  "modified_by",
  "topic_id",
  "message_path_id",
];

const sortableColumns = new Set<string>();

const initialQuery: SearchQueryBody = {
  sort_by: "created",
  sort_direction: "DESC",
};

const createTableState = (
  columns: string[],
  rowsPerPage: number = 25,
): TablePageState => ({
  modalOpen: false,
  rightClickedRow: null,
  sidebar: { isOpen: false, dataset: null },
  columns,
  rowsPerPage: rowsPerPage as 10 | 25 | 50,
  snackbar: { isOpen: false, message: "" },
  sortColumnIndex: 1,
  sortOrder: "descending",
  lastSearchQuery: initialQuery,
  selectedRows: new Set<string>(),
  preferencesLoaded: false,
});

interface TopicMessagePathsTableProps
  extends BaseTableProps<MessagePathRecord> {
  selectedRows?: Set<string>;
  setSelectedRows?: (arg: Set<string>) => void;
  onRowSingleClick?: (msgPath: MessagePathRecord) => void;
  variant?: "elevation" | "outlined";
}

export const TopicMessagePathsTable: React.FC<TopicMessagePathsTableProps> = ({
  data: msgPaths,
  dataCount,
  loading,
  selectedRows,
  setSelectedRows = () => {},
  onRowSingleClick = () => {},
  variant,
  containerStyle,
  fetchMoreData,
}) => {
  const theme = useTheme();
  const {
    preferences,
    saveColumns,
    saveRowsPerPage,
    isLoading: isLoadingPreferences,
    error,
    clearError,
  } = useMessagePathTablePreferences();

  const [state, dispatch] = React.useReducer(
    tablePageReducer,
    createTableState(preferences.columns, preferences.rowsPerPage),
  );

  const [page, setPage] = React.useState(0);

  const msgPathsById = new Map<string, MessagePathRecord>(
    msgPaths.map((msgPath) => {
      return [msgPath.message_path_id, msgPath];
    }),
  );

  const [selectedRowsToggle, setSelectedRowsToggle] =
    React.useState<boolean>(false);

  const pageData = React.useMemo(() => {
    return msgPaths.slice(
      page * state.rowsPerPage,
      page * state.rowsPerPage + state.rowsPerPage,
    );
  }, [msgPaths, state.rowsPerPage, page]);

  const { rows, standardColumns, metadataColumns } = React.useMemo(() => {
    const possibleMetadataColumns: string[] =
      mergeMetadataColumns(pageData, []) ?? [];

    const rows = createRows(pageData, state.columns, possibleMetadataColumns);

    // Sort standard columns alphabetically
    const standardColumns = [...possibleStandardMsgPathsColumns].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );

    // Sort metadata columns alphabetically
    const metadataColumns = [...possibleMetadataColumns].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );

    return { rows, standardColumns, metadataColumns };
  }, [pageData, state.columns]);

  const noSearchResults =
    Object.keys(state.lastSearchQuery).length > 0 && msgPaths.length === 0;

  React.useEffect(() => {
    if (!isLoadingPreferences && !state.preferencesLoaded) {
      dispatch({
        type: "LOAD_PREFERENCES",
        payload: {
          columns: preferences.columns,
          rowsPerPage: preferences.rowsPerPage as 10 | 25 | 50,
        },
      });
    }
  }, [
    isLoadingPreferences,
    preferences.columns,
    preferences.rowsPerPage,
    state.preferencesLoaded,
  ]);

  React.useEffect(() => {
    if (selectedRows && selectedRows.size === 0) {
      setSelectedRowsToggle((prevToggle) => !prevToggle);
      dispatch({
        type: "SET_SELECTED_ROWS",
        payload: { selectedRows: new Set<string>() },
      });
    }
  }, [dispatch, selectedRows]);

  if (!state.preferencesLoaded) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
      }}
    >
      <Box
        sx={{
          flex: 1,
          maxWidth: "100%",
        }}
      >
        <Paper
          sx={{
            mt: theme.spacing(2),
          }}
          variant={variant}
        >
          {error && (
            <Alert
              severity="error"
              onClose={clearError}
              sx={{ mb: theme.spacing(2) }}
            >
              {error instanceof RobotoDomainException
                ? error.message
                : "An unexpected error occurred while saving your preferences. Please try again later."}
            </Alert>
          )}

          {noSearchResults && (
            <Alert severity="info">
              <AlertTitle>Message Paths</AlertTitle>
              <>
                No message paths match your search criteria. Try changing your
                search terms and try again.
              </>
            </Alert>
          )}

          {!noSearchResults && (
            <>
              <RobotoDataGrid
                sortableColumns={sortableColumns}
                sortingOrder={state.sortOrder}
                sortingColumnIndex={state.sortColumnIndex}
                page={page}
                rowsPerPage={state.rowsPerPage}
                nextPageButtonDisabled={
                  dataCount !== undefined
                    ? (page + 1) * state.rowsPerPage >= dataCount
                    : true
                }
                containerStyle={containerStyle}
                loading={loading}
                columnNames={state.columns}
                rows={rows}
                totalRowCount={dataCount}
                onPageChange={(newPage, rowsPerPage) => {
                  if ((newPage + 2) * rowsPerPage >= msgPaths.length) {
                    // Request more pages of data from the backend
                    // as we approach the last page of cached results.
                    // 2 pages prior seemed to work well experimentally.
                    fetchMoreData?.();
                  }
                  setPage(newPage);
                }}
                onRowsPerPageChange={(_page, newRowsPerPage) => {
                  setPage(0);
                  dispatch({
                    type: "SET_TABLE_CONFIG",
                    payload: {
                      rowsPerPage: newRowsPerPage,
                    },
                  });
                  void saveRowsPerPage(newRowsPerPage);
                }}
                onColumnSortClicked={() => {
                  /** noop */
                }}
                onAddColumnClick={() => {
                  dispatch({ type: "SET_MODAL_OPEN", payload: true });
                }}
                onRowDoubleClick={() => {
                  /** noop */
                }}
                onRowSingleClick={(rowId: string) => {
                  const msgPathRecord = msgPathsById.get(rowId);
                  if (onRowSingleClick && msgPathRecord) {
                    onRowSingleClick(msgPathRecord);
                  }
                }}
                onSelectedRowsChange={(selectedRows) => {
                  setSelectedRows(selectedRows);
                  dispatch({
                    type: "SET_SELECTED_ROWS",
                    payload: { selectedRows: selectedRows },
                  });
                }}
                isRowSelectable={false}
                isRowExpandable={true}
                clearSelectedRowsToggle={selectedRowsToggle}
                expandableContent={(rowId: string) => {
                  const msgPathRecord = msgPathsById.get(rowId);

                  if (msgPathRecord) {
                    return (
                      <Box
                        sx={{
                          padding: theme.spacing(3, 2),
                        }}
                      >
                        <MessagePathsDetail
                          topicId={msgPathRecord.topic_id}
                          messagePathIds={[msgPathRecord.message_path_id]}
                        />
                      </Box>
                    );
                  }
                }}
              />
              <ColumnsModal
                open={state.modalOpen}
                handleClose={() => {
                  dispatch({ type: "SET_MODAL_OPEN", payload: false });
                }}
                standardColumns={standardColumns}
                metadataColumns={metadataColumns}
                currentColumns={state.columns}
                onSave={(updatedColumns) => {
                  dispatch({ type: "SAVE_COLUMNS", payload: updatedColumns });
                  void saveColumns(updatedColumns);
                }}
              />
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};
